import React, { useState, useEffect, useRef, useCallback } from 'react';
import { assetUrl } from '../utils/assets';
import { motion, AnimatePresence } from 'motion/react';
import { SpeakerSimpleHigh, SpeakerSimpleSlash, Bell, SpinnerGap, List } from '@phosphor-icons/react';
import { useNarration } from '../context/NarrationContext';
import { useSoundEffects } from '../context/SoundEffectsContext';
import { useBackgroundMusic } from '../context/BackgroundMusicContext';
import NarrationMenu from './NarrationMenu';
import './GameHUD.css';

interface GameHUDProps {
  chapterNumber?: number;
  chapterTitle?: string;
  chapterImage?: string;
  turnsRemaining?: number;
  turnLimit?: number;
  mission: string | null;
  tutorialCompleted: boolean;
  chapterSectionId?: number;
  hidden?: boolean;
  onMenuClick?: () => void;
}

type AnimationType = 'none' | 'decrease' | 'reset';

const GameHUD: React.FC<GameHUDProps> = ({
  chapterNumber,
  chapterTitle,
  chapterImage,
  turnsRemaining,
  turnLimit,
  mission,
  tutorialCompleted,
  chapterSectionId,
  hidden,
  onMenuClick,
}) => {
  const [missionOpen, setMissionOpen] = useState(false);
  const [narrationMenuOpen, setNarrationMenuOpen] = useState(false);
  const [animationType, setAnimationType] = useState<AnimationType>('none');

  const prevTurnsRef = useRef<number | undefined>(undefined);
  const autoCloseTimerRef = useRef<NodeJS.Timeout | null>(null);
  const missionPanelRef = useRef<HTMLDivElement>(null);
  const lastChapterSectionIdRef = useRef<number | undefined>(undefined);

  const { state: narrationState } = useNarration();
  const { state: sfxState } = useSoundEffects();
  const { state: musicState } = useBackgroundMusic();

  // Detect turn changes and trigger animations
  useEffect(() => {
    if (turnsRemaining === undefined) return;
    const prevTurns = prevTurnsRef.current;
    if (prevTurns !== undefined && prevTurns !== turnsRemaining) {
      setAnimationType(turnsRemaining < prevTurns ? 'decrease' : 'reset');
      const timer = setTimeout(() => setAnimationType('none'), 600);
      prevTurnsRef.current = turnsRemaining;
      return () => clearTimeout(timer);
    }
    prevTurnsRef.current = turnsRemaining;
  }, [turnsRemaining]);

  // Auto-open mission on chapter start
  useEffect(() => {
    if (chapterSectionId !== undefined && mission && chapterSectionId !== lastChapterSectionIdRef.current) {
      setMissionOpen(true);
      lastChapterSectionIdRef.current = chapterSectionId;
    }
  }, [chapterSectionId, mission]);

  // Auto-close mission popover after 10s
  useEffect(() => {
    if (missionOpen) {
      autoCloseTimerRef.current = setTimeout(() => setMissionOpen(false), 10000);
    } else if (autoCloseTimerRef.current) {
      clearTimeout(autoCloseTimerRef.current);
      autoCloseTimerRef.current = null;
    }
    return () => {
      if (autoCloseTimerRef.current) {
        clearTimeout(autoCloseTimerRef.current);
        autoCloseTimerRef.current = null;
      }
    };
  }, [missionOpen]);

  // Click-outside to dismiss mission popover
  const handleClickOutside = useCallback((event: MouseEvent) => {
    if (missionOpen && missionPanelRef.current && !missionPanelRef.current.contains(event.target as Node)) {
      setMissionOpen(false);
    }
  }, [missionOpen]);

  const handleEscape = useCallback((event: KeyboardEvent) => {
    if (event.key === 'Escape') {
      setMissionOpen(false);
      setNarrationMenuOpen(false);
    }
  }, []);

  useEffect(() => {
    if (missionOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('keydown', handleEscape);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
        document.removeEventListener('keydown', handleEscape);
      };
    }
  }, [missionOpen, handleClickOutside, handleEscape]);

  const toggleMission = () => {
    setMissionOpen(prev => !prev);
    setNarrationMenuOpen(false);
  };

  const handleMenuClick = () => {
    setMissionOpen(false);
    setNarrationMenuOpen(false);
    onMenuClick?.();
  };

  const handleNarrationClick = () => {
    setNarrationMenuOpen(prev => !prev);
    setMissionOpen(false);
  };

  const getNarrationIcon = () => {
    const iconProps = { size: 20, weight: 'duotone' as const };
    if (narrationState.needsUserActivation) return <Bell {...iconProps} />;
    if (narrationState.status === 'loading') return <SpinnerGap {...iconProps} className="icon-spin" />;
    const anyAudioOn = narrationState.enabled || sfxState.enabled || musicState.enabled;
    return anyAudioOn ? <SpeakerSimpleHigh {...iconProps} /> : <SpeakerSimpleSlash {...iconProps} />;
  };

  const getTurnPillClass = () => {
    if (turnsRemaining === undefined) return '';
    if (turnsRemaining <= 1) return 'turn-pill-critical';
    if (turnsRemaining <= 2) return 'turn-pill-warning';
    return 'turn-pill-normal';
  };

  const showTurnPill = tutorialCompleted && mission && turnsRemaining !== undefined;

  if (hidden) return null;

  return (
    <div className="game-hud-wrapper">
      <div className="game-hud-strip">
        {/* Left: Chapter/turn pill */}
        <div className="hud-left">
          {showTurnPill ? (
            <button
              type="button"
              className={`turn-pill ${getTurnPillClass()}`}
              onClick={toggleMission}
              aria-label="Show mission"
            >
              {chapterImage && (
                <img src={chapterImage} alt="" className="turn-pill-thumb" />
              )}
              <AnimatePresence mode="wait">
                <motion.span
                  key={turnsRemaining}
                  className="turn-pill-number"
                  initial={
                    animationType === 'decrease'
                      ? { scale: 1.4, rotate: -5 }
                      : animationType === 'reset'
                      ? { scale: 0.5, opacity: 0 }
                      : { scale: 1 }
                  }
                  animate={{ scale: 1, rotate: 0, opacity: 1 }}
                  transition={{ type: 'spring', stiffness: 400, damping: 15, duration: 0.4 }}
                >
                  {turnsRemaining}
                </motion.span>
              </AnimatePresence>
              <span className="turn-pill-label">turns</span>
            </button>
          ) : (
            <button
              type="button"
              className="chapter-pill"
              onClick={toggleMission}
              aria-label="Show chapter info"
            >
              {chapterImage && (
                <img src={chapterImage} alt="" className="chapter-pill-thumb" />
              )}
              <span className="chapter-pill-title">{chapterTitle || 'Introduction'}</span>
            </button>
          )}
        </div>

        {/* Right: Narration + Menu directly on strip */}
        <div className="hud-right">
          <div className="narration-btn-wrapper">
            <button
              type="button"
              className="hud-icon-btn"
              onClick={handleNarrationClick}
              aria-label="Audio settings"
            >
              <span className="hud-btn-icon">{getNarrationIcon()}</span>
            </button>
            <NarrationMenu isOpen={narrationMenuOpen} onClose={() => setNarrationMenuOpen(false)} />
          </div>
          <button
            type="button"
            className="hud-icon-btn"
            onClick={handleMenuClick}
            aria-label="Open menu"
          >
            <span className="hud-btn-icon"><List size={20} weight="bold" /></span>
          </button>
        </div>
      </div>

      {/* Mission/Chapter Popover - Dark glass */}
      <AnimatePresence>
        {missionOpen && (
          <motion.div
            ref={missionPanelRef}
            className="mission-popover"
            initial={{ opacity: 0, y: -12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -12 }}
            transition={{ duration: 0.25, ease: [0.22, 1, 0.36, 1] }}
            aria-live="polite"
            aria-label="Chapter info"
          >
            <div className="mission-popover-header">
              {chapterImage ? (
                <img src={chapterImage} alt="" className="mission-popover-icon" />
              ) : chapterNumber ? (
                <img
                  src={assetUrl(`/assets/chapters/mission_${chapterNumber}.png`)}
                  alt=""
                  className="mission-popover-icon"
                  onError={(e) => { e.currentTarget.style.display = 'none'; }}
                />
              ) : null}
              <div className="mission-popover-titles">
                {chapterNumber && <span className="mission-chapter-number">Chapter {chapterNumber}</span>}
                <span className="mission-chapter-title">{chapterTitle || 'Introduction'}</span>
              </div>
            </div>
            {mission && <p className="mission-popover-text">{mission}</p>}
            {turnsRemaining !== undefined && (
              <div className={`mission-popover-turns ${turnsRemaining <= 2 ? 'urgent' : ''}`}>
                <div className="mission-turns-info">
                  <span className="mission-turns-label">Remaining Turns</span>
                  <span className="mission-turns-hint">Chapter resets if you run out</span>
                </div>
                <span className={`mission-turns-value ${
                  turnsRemaining <= 1 ? 'critical' :
                  turnsRemaining <= 2 ? 'warning' : ''
                }`}>
                  {turnsRemaining}
                </span>
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default GameHUD;
